import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getScribbleLobby,
  subscribeScribbleLobby,
  upsertScribblePlayer,
} from "../lib/scribbleLobby";
import { isFirebaseConfigured } from "../lib/firebaseClient";
import { pickRandomAvatarIndex, playersToList } from "../lib/lobbyAvatars";
import { isAnchorModeFromSettings } from "../lib/gameMode";

function readGameSettings() {
  try {
    return JSON.parse(sessionStorage.getItem("gameSettings") || "{}") || {};
  } catch {
    return {};
  }
}

export function useLobbyIdentity() {
  return useMemo(() => {
    const gs = readGameSettings();
    let user = {};
    try {
      user = JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
    } catch {
      user = {};
    }
    const userId = String(user.userId ?? user.userid ?? user.id ?? "").trim();
    const name =
      String(user.name ?? user.fullName ?? user.username ?? user.email ?? "Player").trim() ||
      "Player";
    const lobbyName = String(gs.lobby_name ?? user.lobby_name ?? "").trim();
    const rounds = Math.max(1, Number(gs.rounds ?? gs.questions ?? 3) || 3);
    return { userId, name, lobbyName, rounds, gameSettings: gs, user };
  }, []);
}

/**
 * Join lobby on Firebase and subscribe to live player list + phase.
 */
export function useScribbleLobby({ autoJoin = true } = {}) {
  const { userId, name, lobbyName, rounds } = useLobbyIdentity();
  const [lobby, setLobby] = useState(null);
  const [joining, setJoining] = useState(Boolean(autoJoin));
  const [error, setError] = useState("");
  const joinedRef = useRef(false);

  const players = useMemo(() => playersToList(lobby?.players), [lobby?.players]);
  const phase = lobby?.phase || "lobby";
  const hostUserId = lobby?.hostUserId != null ? String(lobby.hostUserId) : "";
  const isHost = Boolean(userId && hostUserId && userId === hostUserId);
  const roundCount = Math.max(1, Number(lobby?.roundCount ?? rounds) || 3);
  const maxPlayers = Math.max(1, Number(lobby?.maxPlayers) || 10);
  const drawers = Array.isArray(lobby?.drawers) ? lobby.drawers : [];
  const gs = readGameSettings();
  const gameMode = String(
    lobby?.gameMode ||
      (isAnchorModeFromSettings(gs) ? "anchor" : "manager")
  ).toLowerCase();
  const isAnchorMode = gameMode === "anchor";

  const ensureJoined = useCallback(async () => {
    if (!isFirebaseConfigured()) {
      setError("Firebase is not configured.");
      setJoining(false);
      return;
    }
    if (!lobbyName || !userId) {
      setError("Missing lobby or user.");
      setJoining(false);
      return;
    }
    if (joinedRef.current) {
      setJoining(false);
      return;
    }

    setJoining(true);
    setError("");
    try {
      const existing = await getScribbleLobby(lobbyName);
      if (!existing) {
        setError("Lobby has not started yet. Wait for the host.");
        return;
      }
      const cap = Math.max(1, Number(existing.maxPlayers) || 10);
      const hostId =
        existing.hostUserId != null ? String(existing.hostUserId) : "";
      const list = playersToList(existing.players);
      const already = list.some((p) => p.id === userId);
      if (!already) {
        if (list.length >= cap) {
          setError("Lobby is full.");
          return;
        }
        const avatarIndex = pickRandomAvatarIndex(list.map((p) => p.avatarIndex));
        await upsertScribblePlayer(lobbyName, {
          id: userId,
          name,
          avatarIndex,
          isHost: hostId === userId,
          joinedAt: Date.now(),
        });
      }
      joinedRef.current = true;
    } catch (e) {
      setError(e?.message || "Could not join lobby.");
    } finally {
      setJoining(false);
    }
  }, [lobbyName, userId, name]);

  useEffect(() => {
    if (!lobbyName) {
      setLobby(null);
      return undefined;
    }
    const unsub = subscribeScribbleLobby(lobbyName, setLobby);
    return () => unsub();
  }, [lobbyName]);

  useEffect(() => {
    if (!autoJoin) return undefined;
    ensureJoined();
    return undefined;
  }, [autoJoin, ensureJoined]);

  return {
    lobby,
    lobbyName,
    userId,
    name,
    players,
    phase,
    isHost,
    hostUserId,
    roundCount,
    maxPlayers,
    drawers,
    gameMode,
    isAnchorMode,
    joining,
    error,
    ensureJoined,
    configured: isFirebaseConfigured(),
  };
}
